import { FeatureCollection, Point } from 'geojson';
import maplibregl, { Map } from 'maplibre-gl';
import React, { useEffect, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import ButtonPrimary from '@/shared/Button/ButtonPrimary';
import { useCartContext } from '@/context/CartContext';

export type Poi = {
  key: string;
  title?: string;
  numberOfDisplays?: number;
  location: google.maps.LatLngLiteral;
};

function AdboardMap({ pois, allowSelection }: { pois: Poi[], highlightedKeys?: string[], allowSelection?: boolean }) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<Map | null>(null);
  const circleRadiusPx = allowSelection ? 100 : 0;
  const circleSourceId = 'center-circle';
  const { addManyToCart, adBoards } = useCartContext();
  const [showClusters] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;
  
    const map = new maplibregl.Map({
      container: mapRef.current,
      style: 'https://api.maptiler.com/maps/bright-v2/style.json?key=lCDLQDKIdjRx8rpnOptN',
      center: [28, 77],
      zoom: 10,
    });
  
    mapInstance.current = map;
  
    map.on('load', () => {
      setMapReady(true);
    });
  
    return () => map.remove();
  }, []);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !mapReady) return;

    // === Cleanup old layers & sources ===
    ['clusters', 'cluster-count', 'unclustered-point', 'unclustered-labels','circle-layer','center-circle'].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    if (map.getSource('pois')) map.removeSource('pois');
    if (map.getSource(circleSourceId)) map.removeSource(circleSourceId);
    
    const geojson: FeatureCollection<Point, { key: string; numberOfDisplays: number }> = {
      type: 'FeatureCollection',
      features: pois.map(poi => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [poi.location.lng, poi.location.lat],
        },
        properties: {
          key: poi.key,
          numberOfDisplays: poi.numberOfDisplays || 1,
          highlighted: allowSelection ? adBoards.some(adBoard => adBoard.id === poi.key) : [],
        },
      })),
    };

    // Cleanup if exists
    if (map.getLayer('clusters')) map.removeLayer('clusters');
    if (map.getLayer('cluster-count')) map.removeLayer('cluster-count');
    if (map.getLayer('unclustered-point')) map.removeLayer('unclustered-point');
    if (map.getLayer('unclustered-labels')) map.removeLayer('unclustered-labels');
    if (map.getSource('pois')) map.removeSource('pois');

    map.addSource('pois', {
      type: 'geojson',
      data: geojson,
      cluster: showClusters,
      clusterRadius: 50,
      clusterProperties: {
        totalDisplays: ['+', ['get', 'numberOfDisplays']],
      },
    });

    if (showClusters) {
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'pois',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#FF5722',
          'circle-radius': [
            'step',
            ['get', 'totalDisplays'],
            15,
            100,
            25,
            500,
            35,
          ],
        },
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'pois',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{totalDisplays}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }

    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'pois',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['==', ['get', 'highlighted'], true],
          '#4f46e5',  // Default blue
          '#14b8a6', // Gold for highlighted
        ],
        'circle-radius': 8,
      },
    });

    map.addLayer({
      id: 'unclustered-labels',
      type: 'symbol',
      source: 'pois',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': ['get', 'numberOfDisplays'],
        'text-size': 12,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    map.addSource(circleSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [map.getCenter().lng, map.getCenter().lat]
          },
          properties: {}
        }]
      }
    });

    map.addLayer({
      id: 'circle-layer',
      type: 'circle',
      source: circleSourceId,
      paint: {
        'circle-radius': circleRadiusPx,
        'circle-color': '#ff0000',
        'circle-opacity': 0.2
      }
    });

    const updateCenterCircle = () => {
      const center = map.getCenter();
      const source = map.getSource(circleSourceId) as maplibregl.GeoJSONSource;
      source.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [center.lng, center.lat]
          },
          properties: {}
        }]
      });
    };

    map.on('move', updateCenterCircle);

    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = features[0].properties?.cluster_id;

      if (!clusterId) return;

      const currentZoom = map.getZoom();
      const targetZoom = currentZoom + 2;

      map.easeTo({
        //@ts-ignore
        center: features[0].geometry.coordinates as [number, number],
        zoom: targetZoom
      });
    });

    map.on('mouseenter', 'clusters', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'clusters', () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', 'unclustered-point', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'unclustered-point', () => {
      map.getCanvas().style.cursor = '';
    });

  }, [pois, showClusters,adBoards]);

  useEffect(()=>{
    const map = mapInstance.current;
    if (pois?.length && map) {
      const bounds = new maplibregl.LngLatBounds();
      pois.forEach(poi => bounds.extend([poi.location.lng, poi.location.lat]));
      map.fitBounds(bounds, { padding: 50, duration: 1000 });
    }
  },[pois])

  const handlePrintMarkersInsideCircle = () => {
    const map = mapInstance.current;
    if (!map) return;

    const center = map.getCenter();
    const centerPoint = turf.point([center.lng, center.lat]);

    const screenRadiusPx = circleRadiusPx;
    const pointA = map.unproject([map.getContainer().clientWidth / 2, map.getContainer().clientHeight / 2]);
    const pointB = map.unproject([
      map.getContainer().clientWidth / 2 + screenRadiusPx,
      map.getContainer().clientHeight / 2
    ]);

    const radiusInMeters = turf.distance(
      turf.point([pointA.lng, pointA.lat]),
      turf.point([pointB.lng, pointB.lat]),
      { units: 'meters' }
    );

    const insidePOIs = pois.filter(poi => {
      const poiPoint = turf.point([poi.location.lng, poi.location.lat]);
      const dist = turf.distance(centerPoint, poiPoint, { units: 'meters' });
      return dist <= radiusInMeters;
    });

    addManyToCart(insidePOIs.map(item => ({
      id: item.key,
      title: item.title,
    })));
  };

  return (
    <div className='h-full relative'>
      {allowSelection && (
        <div className="absolute w-full text-center top-4">
          <ButtonPrimary onClick={handlePrintMarkersInsideCircle}>
            Add to cart
          </ButtonPrimary>
          {/* <ButtonPrimary onClick={() => setShowClusters(prev => !prev)}>
            {showClusters ? 'Disable Clustering' : 'Enable Clustering'}
          </ButtonPrimary> */}
        </div>
      )}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

export default React.memo(AdboardMap);
